// WeeklyWeather.js
import React from "react";
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  alpha,
} from "@mui/material";
import { useTheme, styled } from "@mui/material/styles";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ThermostatIcon from "@mui/icons-material/Thermostat";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { daysOfWeek, getMoonPhaseIcon } from "./utils/weatherUtils";
import { useUnits } from "../../contexts/UnitContext";

// Glassmorphic Main Card
const GlassCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.8)} 0%, 
    ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
  backdropFilter: "blur(20px) saturate(1.1)",
  WebkitBackdropFilter: "blur(20px) saturate(1.1)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
  borderRadius: 20,
  position: "relative",
  overflow: "hidden",
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.06)}`,
  marginBottom: theme.spacing(3),

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "2px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.primary.main, 0.4)}, 
      ${alpha(theme.palette.secondary.main, 0.3)}, 
      transparent)`,
    borderRadius: "20px 20px 0 0",
    zIndex: 1,
  },

  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 20% 20%, 
      ${alpha(theme.palette.primary.main, 0.03)} 0%, 
      transparent 50%), 
      radial-gradient(circle at 80% 80%, 
      ${alpha(theme.palette.secondary.main, 0.03)} 0%, 
      transparent 50%)`,
    pointerEvents: "none",
    zIndex: 0,
  },

  "& > *": {
    position: "relative",
    zIndex: 2,
  },
}));

// Glassmorphic Day Card
const GlassDayCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.4)} 0%, 
    ${alpha(theme.palette.background.paper, 0.2)} 100%)`,
  backdropFilter: "blur(15px) saturate(1.05)",
  WebkitBackdropFilter: "blur(15px) saturate(1.05)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
  borderRadius: 16,
  position: "relative",
  overflow: "hidden",
  boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.04)}`,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  width: 220,
  flexShrink: 0,

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.primary.main, 0.2)}, 
      ${alpha(theme.palette.secondary.main, 0.15)}, 
      transparent)`,
    borderRadius: "16px 16px 0 0",
    zIndex: 1,
  },

  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 30% 30%, 
      ${alpha(theme.palette.primary.main, 0.02)} 0%, 
      transparent 60%)`,
    pointerEvents: "none",
    zIndex: 0,
  },

  "& > *": {
    position: "relative",
    zIndex: 2,
  },

  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
    background: `linear-gradient(135deg, 
      ${alpha(theme.palette.background.paper, 0.5)} 0%, 
      ${alpha(theme.palette.background.paper, 0.3)} 100%)`,
    borderColor: alpha(theme.palette.primary.main, 0.15),

    "&::before": {
      background: `linear-gradient(90deg, 
        transparent, 
        ${alpha(theme.palette.primary.main, 0.3)}, 
        ${alpha(theme.palette.secondary.main, 0.25)}, 
        transparent)`,
    },
  },
}));

// Glassmorphic Chip
const GlassChip = styled(Chip)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.primary.main, 0.08)} 0%, 
    ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
  backdropFilter: "blur(8px)",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  color: theme.palette.primary.main,
  fontWeight: 500,
  borderRadius: 12,

  "&:hover": {
    background: `linear-gradient(135deg, 
      ${alpha(theme.palette.primary.main, 0.12)} 0%, 
      ${alpha(theme.palette.primary.main, 0.06)} 100%)`,
  },

  "& .MuiChip-icon": {
    color: theme.palette.primary.main,
    filter: `drop-shadow(0 2px 4px ${alpha(theme.palette.primary.main, 0.3)})`,
  },
}));

// Glassmorphic Date Header
const GlassDateHeader = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.primary.main, 0.08)} 0%, 
    ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
  borderRadius: 12,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  position: "relative",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at center, 
      ${alpha(theme.palette.common.white, 0.1)} 0%, 
      transparent 70%)`,
    borderRadius: "inherit",
    pointerEvents: "none",
  },

  "& > *": {
    position: "relative",
    zIndex: 1,
  },
}));

// Glassmorphic Weather Icon Container
const WeatherIconContainer = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.primary.main, 0.06)} 0%, 
    ${alpha(theme.palette.primary.main, 0.03)} 100%)`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
  borderRadius: 16,
  padding: theme.spacing(1),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  width: 70,
  height: 70,
  margin: "0 auto",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at center, 
      ${alpha(theme.palette.common.white, 0.1)} 0%, 
      transparent 70%)`,
    borderRadius: "inherit",
    pointerEvents: "none",
  },
}));

// Glassmorphic Divider
const GlassDivider = styled(Divider)(({ theme }) => ({
  borderColor: alpha(theme.palette.primary.main, 0.15),
  position: "relative",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: "60%",
    height: "1px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.primary.main, 0.3)}, 
      transparent)`,
  },
}));

const WeeklyWeather = ({ weatherData, lastUpdated }) => {
  const theme = useTheme();
  const { unitSystem, convert } = useUnits();

  // Format temperature based on unit preference
  const formatTemperature = (celsius) => {
    if (unitSystem === "metric") {
      return `${Math.round(celsius)}°C`;
    } else {
      // Convert to Fahrenheit
      const fahrenheit = (celsius * 9) / 5 + 32;
      return `${Math.round(fahrenheit)}°F`;
    }
  };

  if (!weatherData || !Array.isArray(weatherData) || weatherData.length === 0) {
    return (
      <GlassCard>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="body1" color="text.secondary" textAlign="center">
            No weekly weather data available.
          </Typography>
        </CardContent>
      </GlassCard>
    );
  }

  return (
    <GlassCard>
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <GlassChip
            icon={<AccessTimeIcon fontSize="small" />}
            label={`Last Updated: ${convert.time.formatDate(
              lastUpdated,
              true
            )}`}
            size="small"
            variant="outlined"
          />
        </Box>

        <Box
          sx={{
            overflowX: "auto",
            pb: 1,
            "&::-webkit-scrollbar": {
              height: 8,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              borderRadius: 4,
            },
            "&::-webkit-scrollbar-thumb": {
              background: `linear-gradient(135deg, 
                ${alpha(theme.palette.primary.main, 0.3)} 0%, 
                ${alpha(theme.palette.primary.main, 0.2)} 100%)`,
              borderRadius: 4,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 2,
              py: 1,
              width: "fit-content",
              minWidth: "100%",
            }}
          >
            {weatherData.map((day, index) => {
              const date = new Date(day.dt * 1000);
              const dayName = daysOfWeek[date.getDay()];
              const sunriseTime = day.sunrise
                ? convert.time.format(new Date(day.sunrise * 1000))
                : "N/A";
              const sunsetTime = day.sunset
                ? convert.time.format(new Date(day.sunset * 1000))
                : "N/A";
              const moonPhase = day.moon_phase;
              const moonriseTime = day.moonrise
                ? convert.time.format(new Date(day.moonrise * 1000))
                : "N/A";
              const moonsetTime = day.moonset
                ? convert.time.format(new Date(day.moonset * 1000))
                : "N/A";
              const tempHigh = day.temp?.max
                ? formatTemperature(day.temp.max)
                : "N/A";
              const tempLow = day.temp?.min
                ? formatTemperature(day.temp.min)
                : "N/A";

              return (
                <GlassDayCard key={index}>
                  <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
                    <GlassDateHeader>
                      <Typography
                        variant="h6"
                        component="div"
                        fontWeight="bold"
                        align="center"
                        sx={{
                          color: "primary.main",
                          filter: `drop-shadow(0 2px 4px ${alpha(
                            theme.palette.primary.main,
                            0.2
                          )})`,
                        }}
                      >
                        {dayName}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mt: 1,
                        }}
                      >
                        <CalendarTodayIcon
                          fontSize="small"
                          sx={{
                            mr: 1,
                            color: "primary.main",
                            filter: `drop-shadow(0 2px 4px ${alpha(
                              theme.palette.primary.main,
                              0.3
                            )})`,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: "primary.main",
                            fontWeight: 500,
                          }}
                        >
                          {convert.time.formatDate(date)}
                        </Typography>
                      </Box>
                    </GlassDateHeader>

                    {day.weather.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <WeatherIconContainer>
                          <img
                            src={`http://openweathermap.org/img/wn/${day.weather[0].icon}@2x.png`}
                            alt={day.weather[0].description}
                            style={{ width: "60px", height: "60px" }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "placeholder.png";
                            }}
                          />
                        </WeatherIconContainer>
                        <Typography
                          variant="body1"
                          color="text.primary"
                          align="center"
                          sx={{
                            textTransform: "capitalize",
                            fontWeight: 500,
                            mt: 1,
                          }}
                        >
                          {day.weather[0].description}
                        </Typography>
                      </Box>
                    )}

                    {day.temp && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-around",
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <ArrowUpwardIcon
                              color="error"
                              fontSize="small"
                              sx={{
                                filter: `drop-shadow(0 2px 4px ${alpha(
                                  theme.palette.error.main,
                                  0.3
                                )})`,
                              }}
                            />
                            <ThermostatIcon
                              color="error"
                              fontSize="small"
                              sx={{
                                filter: `drop-shadow(0 2px 4px ${alpha(
                                  theme.palette.error.main,
                                  0.3
                                )})`,
                              }}
                            />
                          </Box>
                          <Typography
                            variant="h5"
                            component="div"
                            fontWeight="bold"
                            sx={{
                              background: `linear-gradient(135deg, 
                                ${theme.palette.error.main} 0%, 
                                ${theme.palette.error.light} 100%)`,
                              WebkitBackgroundClip: "text",
                              WebkitTextFillColor: "transparent",
                              backgroundClip: "text",
                            }}
                          >
                            {tempHigh}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            High
                          </Typography>
                        </Box>
                        <GlassDivider orientation="vertical" flexItem />
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <ArrowDownwardIcon
                              color="primary"
                              fontSize="small"
                              sx={{
                                filter: `drop-shadow(0 2px 4px ${alpha(
                                  theme.palette.primary.main,
                                  0.3
                                )})`,
                              }}
                            />
                            <ThermostatIcon
                              color="primary"
                              fontSize="small"
                              sx={{
                                filter: `drop-shadow(0 2px 4px ${alpha(
                                  theme.palette.primary.main,
                                  0.3
                                )})`,
                              }}
                            />
                          </Box>
                          <Typography
                            variant="h5"
                            component="div"
                            fontWeight="bold"
                            sx={{
                              background: `linear-gradient(135deg, 
                                ${theme.palette.primary.main} 0%, 
                                ${theme.palette.primary.light} 100%)`,
                              WebkitBackgroundClip: "text",
                              WebkitTextFillColor: "transparent",
                              backgroundClip: "text",
                            }}
                          >
                            {tempLow}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Low
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    <GlassDivider sx={{ my: 2 }} />

                    {day.sunrise && day.sunset && (
                      <Box sx={{ mb: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <WbSunnyIcon
                              color="warning"
                              fontSize="small"
                              sx={{
                                mr: 1,
                                filter: `drop-shadow(0 2px 4px ${alpha(
                                  theme.palette.warning.main,
                                  0.3
                                )})`,
                              }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              Sunrise
                            </Typography>
                          </Box>
                          <Typography variant="body2" fontWeight="medium">
                            {sunriseTime}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <DarkModeIcon
                              color="primary"
                              fontSize="small"
                              sx={{
                                mr: 1,
                                filter: `drop-shadow(0 2px 4px ${alpha(
                                  theme.palette.primary.main,
                                  0.3
                                )})`,
                              }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              Sunset
                            </Typography>
                          </Box>
                          <Typography variant="body2" fontWeight="medium">
                            {sunsetTime}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {day.moon_phase !== undefined && (
                      <Box sx={{ pt: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="h6"
                            component="div"
                            sx={{
                              filter: `drop-shadow(0 2px 4px ${alpha(
                                theme.palette.primary.main,
                                0.3
                              )})`,
                            }}
                          >
                            {getMoonPhaseIcon(moonPhase)}
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            sx={{
                              ml: 1,
                              color: "primary.main",
                            }}
                          >
                            {Math.round(moonPhase * 100)}%
                          </Typography>
                        </Box>
                        {day.moonrise && day.moonset && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-around",
                              fontSize: "0.8rem",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Moonrise
                              </Typography>
                              <Typography variant="body2" fontWeight="medium">
                                {moonriseTime}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Moonset
                              </Typography>
                              <Typography variant="body2" fontWeight="medium">
                                {moonsetTime}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </GlassDayCard>
              );
            })}
          </Box>
        </Box>
      </CardContent>
    </GlassCard>
  );
};

export default WeeklyWeather;
